import { useState } from "react";
import { NavLink, useLocation } from "react-router";
import { useAtom } from "jotai";
import {
  BarChart3,
  Calendar,
  Camera,
  CheckSquare,
  ChevronDown,
  Eye,
  FileText,
  Inbox,
  LayoutDashboard,
  List,
  ListChecks,
  Mail,
  Map,
  PanelLeftClose,
  PanelLeftOpen,
  Settings,
} from "lucide-react";
import { cn } from "@/components/ui";
import { useCurrentRole } from "@/domains/auth";
import type { Role } from "@/domains/auth";
import { sidebarCollapsedAtom } from "@/domains/general/stores/sidebar";

type IconType = React.ComponentType<{ className?: string }>;

type LeafEntry = {
  kind: "link";
  to: string;
  label: string;
  icon: IconType;
  end?: boolean;
  roles: ReadonlyArray<Role>;
};

type GroupChild = {
  to: string;
  label: string;
  icon: IconType;
  end?: boolean;
};

type GroupEntry = {
  kind: "group";
  label: string;
  icon: IconType;
  basePath: string;
  children: ReadonlyArray<GroupChild>;
  roles: ReadonlyArray<Role>;
};

type NavEntry = LeafEntry | GroupEntry;

const NAV_ENTRIES: ReadonlyArray<NavEntry> = [
  {
    kind: "link",
    to: "/",
    label: "Dashboard",
    icon: LayoutDashboard,
    end: true,
    roles: ["requester", "manager", "partner"],
  },
  {
    kind: "link",
    to: "/requests",
    label: "Aanvragen",
    icon: Inbox,
    roles: ["requester", "manager"],
  },
  {
    kind: "link",
    to: "/cases",
    label: "Dossiers",
    icon: FileText,
    roles: ["manager"],
  },
  {
    kind: "group",
    label: "Assets",
    icon: Camera,
    basePath: "/assets",
    roles: ["manager"],
    children: [
      { to: "/assets", label: "Inventaris", icon: List, end: true },
      { to: "/assets/map", label: "Kaart", icon: Map, end: true },
    ],
  },
  {
    kind: "link",
    to: "/tasks",
    label: "Taken",
    icon: CheckSquare,
    roles: ["manager", "partner"],
  },
  {
    kind: "link",
    to: "/follow-up",
    label: "Opvolging",
    icon: Eye,
    roles: ["requester"],
  },
  {
    kind: "link",
    to: "/planning",
    label: "Planning",
    icon: Calendar,
    roles: ["manager"],
  },
  {
    kind: "link",
    to: "/reporting",
    label: "Rapportage",
    icon: BarChart3,
    roles: ["manager"],
  },
  {
    kind: "group",
    label: "Configuratie",
    icon: Settings,
    basePath: "/configuration",
    roles: ["manager"],
    children: [
      { to: "/configuration/asset-types", label: "Asset-types", icon: Camera, end: true },
      {
        to: "/configuration/email-templates",
        label: "E-mail templates",
        icon: Mail,
        end: true,
      },
      {
        to: "/configuration/task-templates",
        label: "Taken templates",
        icon: ListChecks,
        end: true,
      },
    ],
  },
];

function entryIsVisible(entry: NavEntry, role: Role): boolean {
  return entry.roles.includes(role);
}

function pathMatchesBase(pathname: string, basePath: string): boolean {
  return pathname === basePath || pathname.startsWith(`${basePath}/`);
}

export function AppSidebar(): React.JSX.Element {
  const role = useCurrentRole();
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const entries = NAV_ENTRIES.filter((entry) => entryIsVisible(entry, role));

  return (
    <nav
      aria-label="Hoofdnavigatie"
      className={cn(
        "flex flex-col border-r border-border bg-card py-3 transition-[width] duration-200",
        collapsed ? "w-16 items-center px-2" : "w-56 px-3",
      )}
    >
      <ul className="flex flex-col gap-1">
        {entries.map((entry) =>
          entry.kind === "link" ? (
            <li key={entry.to}>
              <SidebarLink entry={entry} collapsed={collapsed} />
            </li>
          ) : (
            <li key={entry.label}>
              <SidebarGroup entry={entry} collapsed={collapsed} />
            </li>
          ),
        )}
      </ul>

      <div
        className={cn(
          "mt-auto border-t border-border pt-3",
          collapsed ? "flex justify-center" : "flex justify-end",
        )}
      >
        <button
          type="button"
          onClick={() => setCollapsed((v) => !v)}
          aria-label={collapsed ? "Sidebar uitklappen" : "Sidebar inklappen"}
          title={collapsed ? "Sidebar uitklappen" : "Sidebar inklappen"}
          className="flex size-9 cursor-pointer items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
        >
          {collapsed ? (
            <PanelLeftOpen className="size-5" aria-hidden />
          ) : (
            <PanelLeftClose className="size-5" aria-hidden />
          )}
        </button>
      </div>
    </nav>
  );
}

type SidebarLinkProps = {
  entry: LeafEntry;
  collapsed: boolean;
};

function SidebarLink({ entry, collapsed }: SidebarLinkProps): React.JSX.Element {
  const { to, label, icon: Icon, end } = entry;
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          "flex items-center rounded-md transition-colors",
          collapsed
            ? "size-10 justify-center"
            : "h-10 gap-3 px-3 text-sm font-medium",
          isActive
            ? "bg-primary-600 text-on-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )
      }
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
    >
      <Icon className="size-5 shrink-0" aria-hidden />
      {!collapsed ? <span className="truncate">{label}</span> : null}
    </NavLink>
  );
}

type SidebarGroupProps = {
  entry: GroupEntry;
  collapsed: boolean;
};

function SidebarGroup({ entry, collapsed }: SidebarGroupProps): React.JSX.Element {
  const { label, icon: Icon, basePath, children } = entry;
  const location = useLocation();
  const isActive = pathMatchesBase(location.pathname, basePath);
  const [open, setOpen] = useState<boolean>(isActive);

  if (collapsed) {
    const primaryChild = children[0];
    return (
      <NavLink
        to={primaryChild.to}
        className={cn(
          "flex size-10 items-center justify-center rounded-md transition-colors",
          isActive
            ? "bg-primary-600 text-on-primary"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}
        aria-label={label}
        title={label}
      >
        <Icon className="size-5" aria-hidden />
      </NavLink>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className={cn(
          "flex h-10 cursor-pointer items-center gap-3 rounded-md px-3 text-sm font-medium transition-colors",
          isActive
            ? "text-foreground"
            : "text-muted-foreground hover:bg-muted hover:text-foreground",
        )}
      >
        <Icon className="size-5 shrink-0" aria-hidden />
        <span className="flex-1 truncate text-left">{label}</span>
        <ChevronDown
          className={cn(
            "size-4 shrink-0 transition-transform",
            open ? "rotate-0" : "-rotate-90",
          )}
          aria-hidden
        />
      </button>

      {open ? (
        <ul className="ml-3 flex flex-col gap-1 border-l border-border pl-3">
          {children.map((child) => (
            <li key={child.to}>
              <NavLink
                to={child.to}
                end={child.end}
                className={({ isActive: childActive }) =>
                  cn(
                    "flex h-9 items-center gap-2 rounded-md px-2 text-sm transition-colors",
                    childActive
                      ? "bg-primary-600 text-on-primary"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                <child.icon className="size-4 shrink-0" aria-hidden />
                <span className="truncate">{child.label}</span>
              </NavLink>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
